import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchProducts,
  updateNurseryPrice,
  updateNurseryOnly,
  bulkUpdateProductFields,
  bulkUpdateVariantFields,
  bulkSetNurseryPrices,
  type ProductsParams,
  type ProductFieldUpdate,
  type VariantFieldUpdate,
} from '@/lib/api';
import { invalidateCatalogQueries } from './catalog-keys';

export function useProducts(params: ProductsParams = {}) {
  return useQuery({
    queryKey: ['products', params],
    queryFn: () => fetchProducts(params),
    placeholderData: (prev) => prev,
    staleTime: 10_000,
  });
}

export function useUpdateNurseryPrice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      shopifyVariantId,
      price,
    }: {
      shopifyVariantId: number;
      price: number | null;
    }) => updateNurseryPrice(shopifyVariantId, price),
    onSuccess: () => invalidateCatalogQueries(queryClient),
  });
}

export function useUpdateNurseryOnly() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      shopifyVariantId,
      nurseryOnly,
    }: {
      shopifyVariantId: number;
      nurseryOnly: boolean;
    }) => updateNurseryOnly(shopifyVariantId, nurseryOnly),
    onSuccess: () => invalidateCatalogQueries(queryClient),
  });
}

export function useBulkSetNurseryPrices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updates: { shopifyVariantId: number; price: number | null }[]) =>
      bulkSetNurseryPrices(updates),
    onSuccess: () => invalidateCatalogQueries(queryClient),
  });
}

/** Apply product-level field edits (status, type, vendor, tags) in bulk + push. */
export function useBulkUpdateProductFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updates: ProductFieldUpdate[]) => bulkUpdateProductFields(updates),
    onSuccess: () => invalidateCatalogQueries(queryClient),
  });
}

/** Apply variant-level field edits (price, SKU, barcode, taxable) in bulk + push. */
export function useBulkUpdateVariantFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updates: VariantFieldUpdate[]) => bulkUpdateVariantFields(updates),
    onSuccess: () => invalidateCatalogQueries(queryClient),
  });
}
